<template>
  <div v-if="showCurves" class="mod-profit-loss">
    <header class="header">
      <span>{{ $t('trade.profit-loss-curves.title') }}</span>
      <icon class="arrow" :name="iconName" @click.native="toggle" />
    </header>
    <PnlAttributes v-if="isExpanded" :attrs="pnlAttrs" />
    <ProfitLossCurves
      v-if="isExpanded"
      :expire-date="expireDate"
      :curve-data="curves"
      :digits="digits"
    />
  </div>
</template>

<script>
import ProfitLossCurves from './ProfitLossCurves'
import PnlAttributes from './PnlAttributes'
import choiceFilter from 'utils/choiceFilter'
import moment from 'moment-timezone'
import Icon from 'com/Icon'
import { ORDER_TYPE } from 'const/trade'

export default {
  name: 'ProfitLoss',
  components: {
    PnlAttributes,
    ProfitLossCurves,
    Icon
  },
  props: {
    context: String, // trade/detail 交易或者详情页面
    position: Number, // 持仓数量
    stock: Object, // 行情信息
    order: Object // 订单信息
  },
  data() {
    return {
      isExpanded: true // true 展开; false 收起
    }
  },
  computed: {
    // 是否是持仓详情页面
    isDetail() {
      return this.context === 'detail'
    },
    showCurves() {
      if (this.isDetail) return true
      // 仅支持市价单和限价单
      if (![ORDER_TYPE.LMT, ORDER_TYPE.MKT].includes(this.order.orderType)) {
        return false
      }
      if (this.order.orderType === ORDER_TYPE.MKT) {
        // 市价单必须有买卖价
        if (this.order.action === 'BUY' && !this.stock.askPrice) return false
        if (this.order.action === 'SELL' && !this.stock.bidPrice) return false
      }
      // 无持仓，开仓
      if (!this.position && this.order.totalQuantity > 0) return true
      // 有持仓，补仓
      if (this.order.action === 'BUY' && this.position > 0) return true
      if (this.order.action === 'SELL' && this.position < 0) return true
      return false
    },
    isCALL() {
      return this.stock.right.toUpperCase() === 'CALL'
    },
    isPUT() {
      return this.stock.right.toUpperCase() === 'PUT'
    },
    isBUY() {
      if (this.isDetail) return this.position > 0
      return this.order.action === 'BUY'
    },
    isSELL() {
      if (this.isDetail) return this.position < 0
      return this.order.action === 'SELL'
    },
    iconName() {
      return this.isExpanded ? 'arrowhead-up' : 'arrowhead-down'
    },
    expireDate() {
      return moment(this.stock.expiry).format('YYYYMMDD')
    },
    // 订单价
    orderPrice() {
      if (this.isDetail) return this.stock.averageCost
      if (this.order.orderType === ORDER_TYPE.LMT) return this.order.limitPrice // 限价单
      if (this.order.orderType === ORDER_TYPE.MKT) {
        // 市价单
        if (this.isBUY) return this.stock.askPrice
        else return this.stock.bidPrice
      }
    },
    digits() {
      return this.stock.digits || choiceFilter(this.stock)
    },
    // 行权价
    strike() {
      return Number(this.stock.strike)
    },
    // 每手股数
    multiplier() {
      return this.stock.multiplier || 1
    },
    // 数量
    quantity() {
      if (this.isDetail) return Math.abs(this.position)
      return this.order.totalQuantity
    },
    // 盈亏平衡点
    pnlBalancePrice() {
      if (this.isCALL) {
        return Number(this.strike) + Number(this.orderPrice)
      } else {
        return Number(this.strike) - Number(this.orderPrice)
      }
    },
    // 行权价点 对应盈亏
    strikeTotalPrice() {
      const price = this.quantity * this.multiplier * this.orderPrice
      return this.$options.filters.floatNum(price, this.stock)
    },
    // 最大收益
    maxProfit() {
      const largeNumber = this.$options.filters.largeNumber
      if (this.isSELL) return largeNumber(this.strikeTotalPrice, this.digits)
      if (this.isPUT) {
        return largeNumber(
          this.pnlBalancePrice * this.quantity * this.multiplier,
          this.digits
        )
      }
      return '+∞'
    },
    // 最大损失
    maxLoss() {
      const largeNumber = this.$options.filters.largeNumber
      if (this.isBUY) return largeNumber(-this.strikeTotalPrice, this.digits)
      if (this.isPUT) {
        return largeNumber(
          -this.pnlBalancePrice * this.quantity * this.multiplier,
          this.digits
        )
      }
      return '-∞'
    },
    curves() {
      // 做多做空
      const isLong = (this.isBUY && this.isCALL) || (this.isSELL && this.isPUT)
      const isShort = (this.isSELL && this.isCALL) || (this.isBUY && this.isPUT)
      const price = Number(this.strikeTotalPrice)
      const p2 = this.isPUT
        ? [this.pnlBalancePrice, 0]
        : [this.strike, this.isBUY ? -price : price]
      const p3 = this.isCALL
        ? [this.pnlBalancePrice, 0]
        : [this.strike, this.isBUY ? -price : price]
      const p1 = [2 * p2[0] - p3[0], isLong ? -price : price]
      const p4 = this.isCALL
        ? [p3[0] * 2.5 - p2[0] * 1.5, (isShort ? -price : price) * 1.5]
        : [p3[0] * 2 - p2[0], isShort ? -price : price]

      return [p1, p2, p3, p4]
    },
    pnlAttrs() {
      const { floatNum } = this.$options.filters
      return [
        {
          key: 'balance',
          name: this.$t('trade.profit-loss-curves.profit-loss-blance'),
          value: floatNum(this.pnlBalancePrice, this.stock)
        },
        {
          key: 'profit',
          name: this.$t('trade.profit-loss-curves.biggest-profit'),
          value: this.maxProfit,
          cls: 'positive'
        },
        {
          key: 'plp',
          name: this.isBUY
            ? this.$t('trade.profit-loss-curves.biggest-loss-point')
            : this.$t('trade.profit-loss-curves.biggest-profit-point'),
          value: floatNum(this.strike, this.stock)
        },
        {
          key: 'loss',
          name: this.$t('trade.profit-loss-curves.biggest-loss'),
          value: this.maxLoss,
          cls: 'negative'
        }
      ]
    }
  },
  methods: {
    toggle() {
      this.isExpanded = !this.isExpanded
    }
  }
}
</script>

<style lang="less" scoped>
.header {
  display: flex;
  padding: 10px 10px 7px;
  justify-content: space-between;
  .icon {
    cursor: pointer;
    color: #838d9e;
    user-select: none;
  }
}
</style>
